import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormGroup,
} from '@angular/forms';
import {
  IDynamicModel,
  IDynamicModelGroup,
  IDynamicModelProperties,
  ITypeaheadSearchFailure,
} from '../models';
import { WindowService } from '@ups/xplat/core';
import { findNestedFormGroupsWith } from '../utils';

/**
 * Walk a FormGroup (and nested groups / FormArrays) for controls with
 * hasSearchError_dynamic === true. Labels resolved from the dynamic model list when
 * provided. ADO #30153.
 * <para>
 * FormArray branch is required for accordion typeaheads: accordion models use
 * formArrayName and children live under array.controls[i] as FormGroups. A walk
 * that only descends FormGroup skips those leaves entirely.
 * </para>
 * Does not mutate the form or block submit.
 * Note: when generateFormControlNames is true, formControlName keys are GUIDs —
 * prefer label for human-readable records.
 * (WI 31925: split out of DynamicRenderService.getTypeaheadSearchFailures, unchanged.)
 */
export function dynamicTypeaheadSearchFailures(
  form: UntypedFormGroup | null | undefined,
  controls?: Array<IDynamicModel | IDynamicModelProperties> | null
): Array<ITypeaheadSearchFailure> {
  if (!form?.controls) {
    return [];
  }
  // Labels must walk the model tree: accordion children live under
  // options.accordion (see validateNoDupeFormControlNames nesting), not as flat
  // top-level siblings. Flat-only lookup falls back to formControlName, which is
  // a GUID when generateFormControlNames is true — useless in the user alert.
  const labelByName = new Map<string, string | undefined>();
  const indexLabels = (
    models: Array<IDynamicModel | IDynamicModelProperties> | null | undefined
  ) => {
    for (const model of models || []) {
      if (!model) {
        continue;
      }
      if (model.formControlName) {
        labelByName.set(model.formControlName, model.label);
      }
      if (model.type === 'accordion' && model.options?.accordion?.length) {
        indexLabels(model.options.accordion);
      }
    }
  };
  indexLabels(controls);
  const failures: Array<ITypeaheadSearchFailure> = [];
  const visitLeaf = (key: string, control: AbstractControl) => {
    if (control && control['hasSearchError_dynamic'] === true) {
      failures.push({
        formControlName: key,
        label: labelByName.get(key),
      });
    }
  };
  const walk = (group: UntypedFormGroup) => {
    for (const key of Object.keys(group.controls)) {
      const control = group.controls[key];
      if (control instanceof UntypedFormGroup) {
        walk(control);
        continue;
      }
      if (control instanceof UntypedFormArray) {
        for (let i = 0; i < control.length; i++) {
          const child = control.at(i);
          if (child instanceof UntypedFormGroup) {
            walk(child);
          } else if (child) {
            visitLeaf(`${key}[${i}]`, child);
          }
        }
        continue;
      }
      visitLeaf(key, control);
    }
  };
  walk(form);
  return failures;
}

/**
 * Checks `form` and any nested FormGroup validity against `modelGroup`
 * (DynamicRenderService.isActiveFormValid, unchanged).
 * @param skipNestedFormGroups skip validating nested form groups
 * @returns failure message if form is invalid
 */
export function dynamicActiveFormInvalidMessage(
  form: UntypedFormGroup,
  modelGroup: IDynamicModelGroup,
  skipNestedFormGroups?: boolean
): string | null {
  let message: string | null = null;
  let reportedNestedGroupError = false;
  const invalidLabels: string[] = [];

  for (const key of Object.keys(form.controls)) {
    const nestedFormGroup = findNestedFormGroupsWith(form, key)[0];
    if (nestedFormGroup) {
      if (reportedNestedGroupError) {
        // prevent numerous nested group validation errors (only 1 is needed at a time)
        break;
      }
      if (!skipNestedFormGroups) {
        for (const nestedKey of Object.keys(nestedFormGroup.controls)) {
          if (!nestedFormGroup.controls[nestedKey].valid) {
            const control = modelGroup.controls.find(
              (c) => c.formArrayName === key
            );
            if (control && control.options?.accordion) {
              const nestedControl = control.options.accordion.find(
                (c) => c.formControlName === nestedKey
              );
              if (nestedControl && !nestedControl.options?.hidden) {
                // limit reported nested gropus to only 1 to prevent long list of errors
                reportedNestedGroupError = true;
                invalidLabels.push(
                  `${nestedControl.label} in ${control.label}`
                );
                break;
              }
            }
          }
        }
      }
    } else if (!form.controls[key].valid && !form.controls[key].disabled) {
      const control = modelGroup.controls.find(
        (c) => c.formControlName === key
      );
      if (control) {
        const validationErrors = form.controls[key].errors;
        const controlErrors = validationErrors
          ? Object.keys(validationErrors)
          : [];
        let customErrorMessage: string | null = null;
        if (controlErrors.length) {
          const firstError = validationErrors?.[controlErrors[0]];

          if (typeof firstError === 'string') {
            // if any errors exist, extra first string type error to display to user
            customErrorMessage = firstError;
          }
        }
        if (customErrorMessage) {
          message = customErrorMessage;
        } else {
          const isHidden = control.options?.hidden === true;

          if (!isHidden) {
            invalidLabels.push(control.label || control.formControlName || key);
          }
        }
      }
    }
  }
  if (invalidLabels.length) {
    message = `To submit the form, please check the validity of the following: ${invalidLabels
      .map((l) => `'${l}'`)
      .join(', ')}`;
  }
  return message;
}

/**
 * The invalid-form alert of DynamicRenderService.isFormValid, moved unchanged. The message is built in a helper
 * that appends to its own copy of the string, so it stays empty and no alert is shown; that is today's behaviour.
 */
export function reportDynamicFormErrors(
  form: UntypedFormGroup,
  win: WindowService
): void {
  const errorMessage = '';
  Object.keys(form.controls).forEach((key) => {
    const controlErrors = form.get(key)?.errors;
    if (controlErrors != null) {
      const errorTypes = Object.keys(controlErrors);
      if (errorTypes && errorTypes.length) {
        for (const errorType of errorTypes) {
          defaultErrorMessage(form, errorMessage, key, errorType);
        }
      }
    }
  });
  if (errorMessage) {
    win.alert(errorMessage);
  }
}

function defaultErrorMessage(
  form: UntypedFormGroup,
  errorMessage: string,
  key: string,
  errorType: string
) {
  errorMessage += `Error '${key}': '${errorType}', value: '${form.controls[key].value}' `;
}
